/**
 * Mounts a game view (teacher, board or student) and feeds it from a
 * SessionClient. This is the glue between the hub and a module's entry
 * point; modules never see the SessionClient.
 */
import type { Role } from '@schulspiele/protocol';

import type { EntryPoint, Mounted, ViewContext } from './game-client';
import { t as sharedT } from './i18n/de';
import { mediaUrl, preloadMedia } from './media';
import type { SessionClient } from './ws-client';

export function mountGameView(
  entry: EntryPoint,
  mount: HTMLElement,
  client: SessionClient,
  role: Role,
  actorId: string,
  moduleT?: (key: string, params?: Record<string, string | number>) => string | undefined,
): () => void {
  const context: ViewContext = {
    role,
    // Getters: both change on a reconnect (new hello, maybe a new join).
    get sessionId() {
      return client.sessionId ?? undefined;
    },
    actorId,
    send: (intent) => client.sendIntent(intent),
    onEvent: (handler) => client.on('event', handler),
    get clockOffset() {
      return client.clockOffset;
    },
    // Media play on the board only. A phone gets the pictures a task is
    // answered on (Bildpunkt), in the phone size, with its own key.
    mediaUrl: (id, variant) => {
      if (role !== 'student')
        return mediaUrl(id, variant ?? 'original', client.lastState?.mediaKey);
      const key = client.lastState?.studentMediaKey;
      return key === undefined ? '' : mediaUrl(id, 'phone', key);
    },
    preloadMedia: (ids, onProgress) =>
      role === 'student'
        ? Promise.resolve()
        : preloadMedia(
            ids.map((id) => mediaUrl(id, 'original', client.lastState?.mediaKey)),
            onProgress,
          ),
    onSession: (handler) => {
      const send = (): void => {
        const st = client.lastState;
        if (st !== null) handler({ phase: st.phase, gameOver: st.gameOver });
      };
      send();
      return client.on('state', send);
    },
    t: (key, params) =>
      moduleT?.(key, params) ?? sharedT(key as Parameters<typeof sharedT>[0], params),
  };

  const mounted: Mounted = entry(mount, context);
  if (client.lastView !== null) mounted.update(client.lastView);
  const off = client.on('view', (view) => {
    mounted.update(view);
  });
  return () => {
    off();
    mounted.destroy();
  };
}
